import {
  ProductCustomizable,
  AddProductsToCartError,
  AddProductsToCartQuantity,
} from '@graphcommerce/magento-product'
import { BundleProductOptions } from '@graphcommerce/magento-product-bundle'
import {
  ConfigurableName,
  ConfigurableProductOptions,
  ConfigurablePrice,
} from '@graphcommerce/magento-product-configurable'
import { DownloadableProductOptions } from '@graphcommerce/magento-product-downloadable'
import { ProductReviewChip } from '@graphcommerce/magento-review'
import { ProductWishlistChipDetail } from '@graphcommerce/magento-wishlist'
import { isTypename, responsiveVal } from '@graphcommerce/next-ui'
import { Trans } from '@lingui/react'
import { Box, Link, Typography } from '@mui/material'
import { Product } from '../../../lib/types'
import { CallADruggistQuery } from '../../Dialogs/MoreInformationDialog/CallADruggist.gql'
import { MedicalLabel } from '../MedicalLabel/MedicalLabel'
import { Money } from '../Money'
import { AddProductsToCartButtonWithModalWrapper } from '../ProductAddToCart/AddProductsToCartButtonWithModalWrapper'
import { useGetCurrentProductQtyFromCart } from '../ProductAddToCart/useGetCurrentProductQtyFromCart'
import { ProductPagePromotionWrapper } from '../PromotionLabel/ProductPagePromotionWrapper'

type PromotionWrapperProps = CallADruggistQuery & {
  product: Product
}

export const PromotionWrapper = (props: PromotionWrapperProps) => {
  const { product, callADruggist, infoForm } = props
  const { sku, has_cart_rule, has_catalog_rule, price_range, mos_rvg_code, mos_rvh_code } = product

  const maxQty = 10
  const { cartItemQty } = useGetCurrentProductQtyFromCart({ sku })
  const remainingQty = cartItemQty ? maxQty - cartItemQty : maxQty
  const isPromotionProduct = Boolean(has_cart_rule || has_catalog_rule)

  return (
    <>
      <>
        {isTypename(product, ['ConfigurableProduct', 'BundleProduct']) && (
          <Typography component='div' variant='body2' color='text.disabled'>
            <Trans
              id='As low as <0/>'
              components={{
                0: <Money {...price_range.minimum_price.final_price} />,
              }}
            />
          </Typography>
        )}
        <MedicalLabel
          callADruggist={callADruggist}
          mos_rvg_code={mos_rvg_code}
          mos_rvh_code={mos_rvh_code}
        />
        <Typography variant='h3' component='div' gutterBottom>
          {isTypename(product, ['ConfigurableProduct']) ? (
            <ConfigurableName product={product} />
          ) : (
            product?.name
          )}
        </Typography>
        <ProductReviewChip rating={product.rating_summary} reviewSectionId='reviews' />
      </>
      {isTypename(product, ['ConfigurableProduct']) && (
        <ConfigurableProductOptions
          product={product}
          optionEndLabels={{
            size: (
              <Link
                href='/modal/product/global/size'
                rel='nofollow'
                component='button'
                type='button'
                color='primary'
                underline='hover'
              >
                <Trans id='Which size is right?' />
              </Link>
            ),
          }}
        />
      )}
      {isTypename(product, ['BundleProduct']) && (
        <BundleProductOptions product={product} layout='stack' />
      )}
      {isTypename(product, ['DownloadableProduct']) && (
        <DownloadableProductOptions product={product} />
      )}
      {!isTypename(product, ['GroupedProduct']) && <ProductCustomizable product={product} />}
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          columnGap: theme.spacings.md,
        })}
      >
        <AddProductsToCartError>
          <Box
            sx={{
              width: '100%',
              mt: { md: 3 },
              mb: 3,
              display: 'grid',
              gridTemplateAreas: `
                      "favorite price"
                      "quantity addToCart"
                      "message message"
              `,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                width: responsiveVal(125, 145),
                justifyContent: 'space-between',
              }}
            >
              <ProductWishlistChipDetail
                name={product.name}
                sku={product.sku}
                url_key={product.url_key}
                buttonProps={{ size: 'small' }}
                sx={{ gridArea: 'favorite', p: '8px', mb: '-6px' }}
              />
              {product?.qty_pieces && product?.qty_unit && (
                <Typography variant='body2' color='text.secondary' textAlign='right'>
                  {product.qty_pieces} {product.qty_unit}
                </Typography>
              )}
            </Box>

            <AddProductsToCartQuantity
              variant='standard'
              size='small'
              color='secondary'
              inputProps={{
                max: isPromotionProduct ? remainingQty : undefined,
                onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                  const currentQty = parseInt(event.target.value, 10)
                  const minQty = parseInt(event.target.min, 10)

                  if (currentQty <= minQty || Number.isNaN(currentQty)) {
                    event.target.value = event.target.min ?? '1'
                  }
                },
              }}
              InputProps={{ disableUnderline: true }}
              sx={{
                display: 'flex',
                gridArea: 'quantity',
                justifyContent: 'flex-end',
                ml: '12px',
                '& .MuiInput-input': {
                  borderBottom: '1px solid',
                  borderTop: '1px solid',
                  borderColor: 'inherit',
                  borderRadius: 0,
                  minWidth: responsiveVal(40, 50),
                  p: 0,
                  height: 'calc(100% - 2px)',
                },
                '& .MuiIconButton-root': {
                  border: '1px solid',
                  borderColor: 'inherit',
                  p: 2,
                  '&:hover': { bgcolor: 'primary.main', '& svg': { color: 'white' } },
                  '&:nth-of-type(1)': {
                    borderRadius: ' 40px 0 0 40px',
                  },
                  '&:nth-of-type(2)': {
                    borderRadius: '0 12rem 12rem 0',
                  },
                },
              }}
            />

            <Typography component='div' variant='h3' lineHeight='1' sx={{ gridArea: 'price' }}>
              {isTypename(product, ['ConfigurableProduct']) ? (
                <ConfigurablePrice product={product} />
              ) : (
                <>
                  {price_range.minimum_price.regular_price.value !==
                    price_range.minimum_price.final_price.value && (
                    <Money
                      {...price_range.minimum_price.regular_price}
                      sx={{ '& .regular-price > .MuiBox-root': { ml: 'auto' } }}
                    />
                  )}
                  <Money isSpecialPrice {...product.price_range.minimum_price.final_price} />
                </>
              )}
            </Typography>
            <AddProductsToCartButtonWithModalWrapper
              product={product}
              infoForm={infoForm}
              callADruggist={callADruggist}
            />
          </Box>
        </AddProductsToCartError>
      </Box>
      <ProductPagePromotionWrapper product={product} />
    </>
  )
}
